import { Environment, XID } from "@atlassian/atl-cross-domain-tracking";
import AnalyticsWebClient, {
    envType,
    userType
} from "@atlassiansox/analytics-web-client";

import { Cookie } from "@/utils/cookie";

const getAnonIdUsedByFeatureGatesClientInSSR = (): string =>
    window.featureGateBootstrapValues?.identifiers?.analyticsAnonymousId ?? "";

const getAtlassianUserId = () => {
    const userId = Cookie.readCookie("__aid_user_id");
    if (userId) {
        return decodeURIComponent(userId);
    }
    return null;
};

const getGASv3Environment = (): string => {
    switch (process.env["CLIENT_LIBRARY_ENV"]) {
        case "production":
            return envType.PROD;
        case "staging":
            return envType.STAGING;
        case "development":
            return envType.DEV;
        default:
            return envType.LOCAL;
    }
};

const getXidEnvironment = (): Environment => {
    switch (process.env["CLIENT_LIBRARY_ENV"]) {
        case "production":
            return Environment.PROD;
        case "staging":
            return Environment.STAGE;
        case "development":
            return Environment.DEV;
        default:
            return Environment.LOCAL;
    }
};

const shouldUseStargate = (): boolean => {
    switch (process.env["CLIENT_LIBRARY_ENV"]) {
        case "production":
        case "staging":
            return true;
        case "development":
        default:
            return false;
    }
};

const getBuildVersion = () => {
    return process.env["VERSION"] || "1.0.0";
};

export const getAnalyticsWebClient = (): AnalyticsWebClient => {
    const client = new AnalyticsWebClient(
        {
            env: getGASv3Environment(),
            product: "marketing",
            version: getBuildVersion(),
            locale: "en-us"
        },
        {
            useStargate: shouldUseStargate(),
            xidConsent: true,
            xidPromiseFn: () =>
                new XID(getXidEnvironment()).getXidCallbackForPromise(),
            // If SSR wasn't given an anonymous ID cookie, then it will generate one and pass it down.
            // This function will ensure the analytics client uses the same one instead of generating another.
            customAnonymousIdGenerator: getAnonIdUsedByFeatureGatesClientInSSR,
            // Default is 500, reducing to increase reliabilty of events fired before redirect
            flushWaitInterval: 100
        }
    );

    const aaid = getAtlassianUserId();
    if (aaid) {
        client.setUserInfo(userType.ATLASSIAN_ACCOUNT, aaid);
    }

    return client;
};
